/**
 * Canvas描画ユーティリティ
 *
 * 線分二等分課題で使用するCanvas描画関数群
 */

const CANVAS_CONFIG = {
  BACKGROUND_COLOR: '#ffffff',
  LINE_COLOR: '#000000',
  MARK_COLOR: '#ff0000',
  MARK_THICKNESS: 2,
  MARK_LENGTH: 30,
} as const;

/**
 * 線分を描画
 *
 * @param ctx - Canvas 2D コンテキスト
 * @param x1 - 開始X座標（px）
 * @param y1 - 開始Y座標（px）
 * @param x2 - 終了X座標（px）
 * @param y2 - 終了Y座標（px）
 * @param thickness - 線の太さ（px）
 * @param color - 線の色（オプション）
 */
export function drawLine(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  thickness: number,
  color: string = CANVAS_CONFIG.LINE_COLOR
): void {
  ctx.save();
  ctx.beginPath();
  ctx.strokeStyle = color;
  ctx.lineWidth = thickness;
  // 太さ変更時に左右へはみ出さないよう、端の丸みを無効化
  ctx.lineCap = 'butt';
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
  ctx.restore();
}

/**
 * マーカーを描画（縦線）
 *
 * @param ctx - Canvas 2D コンテキスト
 * @param x - マーカーX座標（px）
 * @param y - マーカーY座標（px）
 * @param length - マーカーの長さ（px）
 * @param thickness - マーカーの太さ（px）
 * @param color - マーカーの色（オプション）
 */
export function drawMarker(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  length: number = CANVAS_CONFIG.MARK_LENGTH,
  thickness: number = CANVAS_CONFIG.MARK_THICKNESS,
  color: string = CANVAS_CONFIG.MARK_COLOR
): void {
  ctx.save();
  ctx.beginPath();
  ctx.strokeStyle = color;
  ctx.lineWidth = thickness;
  ctx.lineCap = 'round';
  // 縦線として描画（線分の中心を基準に上下に伸ばす）
  ctx.moveTo(x, y - length / 2);
  ctx.lineTo(x, y + length / 2);
  ctx.stroke();
  ctx.restore();
}

/**
 * Canvasをクリア
 *
 * @param ctx - Canvas 2D コンテキスト
 * @param width - Canvas幅（px）
 * @param height - Canvas高さ（px）
 * @param backgroundColor - 背景色（オプション）
 */
export function clearCanvas(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  backgroundColor: string = CANVAS_CONFIG.BACKGROUND_COLOR
): void {
  ctx.save();
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, width, height);
  ctx.restore();
}

/**
 * ランダムな線分位置を生成
 *
 * @param canvasWidthMm - Canvas幅（mm）
 * @param canvasHeightMm - Canvas高さ（mm）
 * @param lineLengthMm - 線分の長さ（mm）
 * @param marginMm - 端からのマージン（mm）、デフォルト20mm
 * @returns 線分の中心位置（mm）
 */
export function getRandomLinePosition(
  canvasWidthMm: number,
  canvasHeightMm: number,
  lineLengthMm: number,
  marginMm: number = 20
): { x: number; y: number } {
  // 線分が画面内に収まるように位置を計算
  // 線の中心位置なので、線分の半分の長さを考慮
  const halfLength = lineLengthMm / 2;

  // X座標の範囲: マージン + 線の半分 ~ Canvas幅 - マージン - 線の半分
  const minX = marginMm + halfLength;
  const maxX = canvasWidthMm - marginMm - halfLength;

  // Y座標の範囲: マージン ~ Canvas高さ - マージン
  const minY = marginMm;
  const maxY = canvasHeightMm - marginMm;

  // ランダム位置を生成
  const x = minX + Math.random() * (maxX - minX);
  const y = minY + Math.random() * (maxY - minY);

  return { x, y };
}

/**
 * 線分の両端座標を計算（水平線用）
 *
 * @param centerX - 中心X座標（mm）
 * @param centerY - 中心Y座標（mm）
 * @param length - 線分の長さ（mm）
 * @returns 線分の開始点と終了点の座標（mm）
 */
export function calculateLineEndpoints(
  centerX: number,
  centerY: number,
  length: number
): { x1: number; y1: number; x2: number; y2: number } {
  const halfLength = length / 2;
  return {
    x1: centerX - halfLength,
    y1: centerY,
    x2: centerX + halfLength,
    y2: centerY,
  };
}

/**
 * タッチイベントからCanvas座標を取得
 *
 * @param event - タッチイベントまたはマウスイベント
 * @param canvas - Canvas要素
 * @returns Canvas上の座標（px）
 */
export function getCanvasCoordinates(
  event: TouchEvent | MouseEvent,
  canvas: HTMLCanvasElement
): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  let clientX: number;
  let clientY: number;

  if ('touches' in event && event.touches.length > 0) {
    // タッチイベント
    clientX = event.touches[0].clientX;
    clientY = event.touches[0].clientY;
  } else if ('changedTouches' in event && event.changedTouches.length > 0) {
    // タッチ終了イベント
    clientX = event.changedTouches[0].clientX;
    clientY = event.changedTouches[0].clientY;
  } else {
    // マウスイベント
    clientX = (event as MouseEvent).clientX;
    clientY = (event as MouseEvent).clientY;
  }

  // Canvas内の相対座標を計算
  // デバイスピクセル比を考慮
  const scaleX = canvas.width / rect.width;
  const scaleY = canvas.height / rect.height;

  return {
    x: (clientX - rect.left) * scaleX,
    y: (clientY - rect.top) * scaleY,
  };
}

/**
 * 偏差を計算（マーク位置 - 真の中心位置）
 *
 * @param markedX - マーク位置X（mm）
 * @param lineCenterX - 線分中心X（mm）
 * @param lineLength - 線分の長さ（mm）
 * @returns 偏差（mm）とパーセンテージ（%）
 */
export function calculateDeviation(
  markedX: number,
  lineCenterX: number,
  lineLength: number
): { deviationMm: number; deviationPercent: number } {
  const deviationMm = markedX - lineCenterX;
  const deviationPercent = (deviationMm / lineLength) * 100;

  return {
    deviationMm: Math.round(deviationMm * 100) / 100, // 小数点2桁
    deviationPercent: Math.round(deviationPercent * 100) / 100, // 小数点2桁
  };
}

/**
 * 異常判定
 *
 * @param deviationPercent - 偏差パーセンテージ
 * @param threshold - 閾値（%）
 * @returns 異常かどうか
 */
export function isAbnormal(
  deviationPercent: number,
  threshold: number
): boolean {
  return Math.abs(deviationPercent) > threshold;
}

/**
 * 複数線分の位置を重ならないように生成
 *
 * 画面を均等に分割してY座標を配置し、X座標にランダム性を持たせる。
 * 線分間の最小距離を確保して重なりを防止する。
 *
 * @param canvasWidthMm - Canvas幅（mm）
 * @param canvasHeightMm - Canvas高さ（mm）
 * @param lineLengthsMm - 各線分の長さ（mm）の配列
 * @param marginMm - 端からのマージン（mm）、デフォルト20mm
 * @param minVerticalGapMm - 線分間の最小垂直距離（mm）、デフォルト15mm
 * @returns 各線分の中心位置（mm）の配列
 */
export function generateMultiLinePositions(
  canvasWidthMm: number,
  canvasHeightMm: number,
  lineLengthsMm: number[],
  marginMm: number = 20,
  minVerticalGapMm: number = 15,
  layout: 'random' | 'center' | 'left' | 'right' = 'random'
): { x: number; y: number }[] {
  const count = lineLengthsMm.length;

  // 使用可能な縦方向の範囲
  const usableTop = marginMm;
  const usableBottom = canvasHeightMm - marginMm;
  const usableHeight = usableBottom - usableTop;

  const positions: { x: number; y: number }[] = [];

  for (let i = 0; i < count; i++) {
    const lengthMm = lineLengthsMm[i];
    const halfLength = lengthMm / 2;

    // Y座標: 均等分割
    const baseY = usableTop + (usableHeight / (count + 1)) * (i + 1);
    let y: number;
    if (layout === 'center') {
      // 均等配置: ジッターなし
      y = baseY;
    } else {
      // ランダム/左寄せ/右寄せ: Y座標にジッターを加える
      const maxJitter = Math.min(
        usableHeight / (count + 1) / 2 - minVerticalGapMm / 2,
        10
      );
      const jitter = maxJitter > 0 ? (Math.random() - 0.5) * 2 * maxJitter : 0;
      y = Math.max(usableTop, Math.min(usableBottom, baseY + jitter));
    }

    // X座標: レイアウトに応じた配置
    const minX = marginMm + halfLength;
    const maxX = canvasWidthMm - marginMm - halfLength;
    const range = Math.max(0, maxX - minX);
    let x: number;
    switch (layout) {
      case 'left':
        x = minX + Math.random() * range * 0.25;
        break;
      case 'right':
        x = minX + range * 0.75 + Math.random() * range * 0.25;
        break;
      case 'center':
        x = canvasWidthMm / 2;
        break;
      default:
        x = minX + Math.random() * range;
        break;
    }

    positions.push({ x, y });
  }

  return positions;
}

/**
 * 座標から最も近い線分を見つける
 *
 * @param xPx - タッチ/クリックのX座標（px）
 * @param yPx - タッチ/クリックのY座標（px）
 * @param lines - 線分情報の配列
 * @param maxDistancePx - 線分との最大許容距離（px）
 * @returns 最も近い線分のインデックス。範囲外の場合は -1
 */
export function findNearestLine(
  xPx: number,
  yPx: number,
  lines: { x1Px: number; y1Px: number; x2Px: number; y2Px: number; marked?: boolean }[],
  maxDistancePx: number = 80
): number {
  let minDistance = Infinity;
  let nearestIndex = -1;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    // マーク済みの線分はスキップ
    if (line.marked) continue;

    // 線分（水平線）からの垂直距離
    const lineCenterY = (line.y1Px + line.y2Px) / 2;
    const verticalDist = Math.abs(yPx - lineCenterY);

    // X座標が線分の範囲内かチェック（少し余裕を持たせる）
    const xMargin = 20; // px
    const inXRange = xPx >= line.x1Px - xMargin && xPx <= line.x2Px + xMargin;

    if (inXRange && verticalDist < maxDistancePx && verticalDist < minDistance) {
      minDistance = verticalDist;
      nearestIndex = i;
    }
  }

  return nearestIndex;
}
